/**
 * CobrarEnDolares — la página del contratista. PÚBLICA, sin sesión.
 *
 * Es el único sitio donde se puede escribir la dirección: la empresa no puede,
 * ni siquiera queriendo. Ese es el control, no un detalle de implementación.
 *
 * Está escrita para alguien que quizá nunca ha recibido USDC. Por eso el texto
 * dice qué pasa después en cada paso, y por eso la advertencia de la red va
 * ANTES de que pegue nada — no como un error después de equivocarse.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = import.meta.env.VITE_INVESTMENTS_API_URL || '';
const url = (t, sufijo = '') => `${API}/api/business-freelancers/invitacion/${t}${sufijo}`;

export default function CobrarEnDolares() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState('');
  const [direccion, setDireccion] = useState('');
  const [msg, setMsg] = useState('');
  const [aviso, setAviso] = useState('');
  const [cargando, setCargando] = useState(false);

  const traer = () => axios.get(url(token))
    .then((r) => setInv(r.data.data))
    .catch((e) => setErr(e?.response?.data?.message || 'No pude abrir tu invitación.'));

  useEffect(() => { traer(); }, [token]);

  async function guardar() {
    setMsg(''); setAviso(''); setCargando(true);
    try {
      const r = await axios.post(url(token, '/direccion'), { direccion: direccion.trim() });
      // 'sin_actividad' = billetera nueva. No es un error, pero se avisa: es donde
      // más suele haber un error de copiado que nadie nota hasta la prueba.
      if (r.data?.data?.actividad === 'sin_actividad') {
        setAviso('Esa dirección no tiene movimientos todavía. Si es nueva, perfecto. Si no, revísala bien antes de seguir.');
      }
      setDireccion('');
      await traer();
    } catch (e) {
      setMsg(e?.response?.data?.message || 'No pude guardar la dirección.');
    } finally { setCargando(false); }
  }

  async function confirmar() {
    setMsg(''); setCargando(true);
    try {
      await axios.post(url(token, '/confirmar-prueba'));
      await traer();
    } catch (e) {
      setMsg(e?.response?.data?.message || 'No pude confirmar.');
    } finally { setCargando(false); }
  }

  const Marco = ({ children }) => (
    <div className="min-h-screen bg-[#F9FAFB] flex items-start justify-center px-4 py-10">
      <div className="w-full max-w-lg">{children}</div>
    </div>
  );

  if (err) return <Marco><div className="rounded-xl bg-white border border-[#FECACA] p-6 text-[#991B1B]">{err}</div></Marco>;
  if (!inv) return <Marco><p className="text-[#6B7280]">Abriendo tu invitación…</p></Marco>;

  return (
    <Marco>
      <div className="rounded-2xl bg-white border border-[#E5E7EB] p-6 md:p-8">
        <h1 className="text-2xl font-bold text-[#0a2540]">Hola {inv.nombre}</h1>
        <p className="text-[#6B7280] mt-1">
          <strong className="text-[#111827]">{inv.empresa}</strong> quiere pagarte en dólares por blockchain.
        </p>

        {/* CONFIRMADO */}
        {inv.estado === 'confirmado' && (
          <div className="mt-6">
            <div className="rounded-xl bg-[#ECFDF3] border border-[#A7F3D0] p-4">
              <p className="font-semibold text-[#047857]">✅ Todo listo</p>
              <p className="text-sm text-[#065F46] mt-1">
                Tu dirección quedó habilitada. {inv.empresa} ya puede enviarte tus pagos aquí.
              </p>
            </div>
            <p className="mt-4 text-xs text-[#6B7280] break-all">
              Red {inv.red} · <span className="font-mono">{inv.direccion}</span>
            </p>
            <p className="mt-4 text-sm text-[#6B7280]">
              ¿Cambiaste de billetera? Pega la nueva abajo. Por seguridad quedará
              en espera dos días antes de recibir pagos, y te avisaremos.
            </p>
            <Formulario {...{ direccion, setDireccion, guardar, cargando, etiqueta: 'Cambiar mi dirección' }} />
          </div>
        )}

        {/* ESPERANDO QUE CONFIRME LA PRUEBA */}
        {inv.estado === 'probando' && (
          <div className="mt-6">
            <div className="rounded-xl bg-[#FFFBEB] border border-[#FDE68A] p-4">
              <p className="font-semibold text-[#92400E]">Te enviamos US${inv.montoPrueba} de prueba</p>
              <p className="text-sm text-[#78350F] mt-1">
                Revisa tu billetera. Puede tardar unos minutos. Cuando lo veas,
                confírmanos aquí y tu dirección queda habilitada.
              </p>
            </div>
            <p className="mt-3 text-xs text-[#6B7280] break-all font-mono">{inv.direccion}</p>
            <button onClick={confirmar} disabled={cargando}
              className="mt-4 w-full rounded-lg bg-[#092F7B] text-white font-semibold py-3 disabled:opacity-40">
              {cargando ? 'Confirmando…' : `Sí, me llegaron los US$${inv.montoPrueba}`}
            </button>
            <p className="mt-3 text-sm text-[#6B7280]">
              ¿No te llegó? Escríbele a {inv.empresa}. <strong>No confirmes hasta que lo veas</strong> —
              es lo que evita que tus pagos se vayan a una dirección equivocada.
            </p>
          </div>
        )}

        {/* AÚN NO HA REGISTRADO, O YA REGISTRÓ Y FALTA LA PRUEBA */}
        {['invitado', 'por_probar', 'enfriamiento'].includes(inv.estado) && (
          <div className="mt-6">
            {inv.estado === 'por_probar' && (
              <div className="rounded-xl bg-[#EFF6FF] border border-[#BFDBFE] p-4 mb-4">
                <p className="text-sm text-[#1E40AF]">
                  Ya guardamos tu dirección. Falta que {inv.empresa} te envíe los
                  US${inv.montoPrueba} de prueba — te avisaremos.
                </p>
                <p className="text-xs text-[#1E3A8A] mt-2 font-mono break-all">{inv.direccion}</p>
              </div>
            )}
            {inv.estado === 'enfriamiento' && (
              <div className="rounded-xl bg-[#FFFBEB] border border-[#FDE68A] p-4 mb-4 text-sm text-[#92400E]">
                Cambiaste tu dirección hace poco. Por seguridad está en espera unos
                días antes de recibir pagos. Si tú no hiciste ese cambio, avísale
                a {inv.empresa} ahora mismo.
              </div>
            )}

            <div className="rounded-xl bg-[#FEF2F2] border border-[#FECACA] p-4">
              <p className="font-semibold text-[#991B1B]">Antes de pegar tu dirección</p>
              <p className="text-sm text-[#991B1B] mt-1">
                Necesitamos tu dirección de <strong>USDC en la red Solana</strong>.
                En tu billetera o exchange, al pedir la dirección para recibir,
                elige <strong>Solana</strong> — no Ethereum, no BSC, no Polygon.
              </p>
              <p className="text-sm text-[#991B1B] mt-2">
                Si la red no coincide, <strong>el dinero se pierde y no hay forma de recuperarlo.</strong>
              </p>
            </div>

            <Formulario {...{ direccion, setDireccion, guardar, cargando, etiqueta: 'Guardar mi dirección' }} />

            <div className="mt-5 text-sm text-[#6B7280]">
              <p className="font-semibold text-[#111827]">Qué pasa después</p>
              <ol className="mt-2 space-y-1 list-decimal list-inside">
                <li>{inv.empresa} te envía <strong>US${inv.montoPrueba}</strong> de prueba.</li>
                <li>Cuando lo veas en tu billetera, vuelves aquí y confirmas.</li>
                <li>Listo: desde ahí recibes tus pagos completos.</li>
              </ol>
              <p className="mt-3">
                <strong>{inv.empresa} no puede escribir ni cambiar tu dirección.</strong> Solo tú, desde este enlace.
              </p>
            </div>
          </div>
        )}

        {msg && <p className="mt-4 text-sm text-[#991B1B]">{msg}</p>}
        {aviso && <p className="mt-4 text-sm text-[#B54708]">{aviso}</p>}
      </div>
      <p className="text-center text-xs text-[#9CA3AF] mt-4">FlowexPay</p>
    </Marco>
  );
}

function Formulario({ direccion, setDireccion, guardar, cargando, etiqueta }) {
  return (
    <div className="mt-4">
      <label className="block text-sm font-medium text-[#374151]">Tu dirección de USDC (Solana)</label>
      <input
        value={direccion}
        onChange={(e) => setDireccion(e.target.value.trim())}
        placeholder="Pega aquí la dirección completa"
        className="mt-1 w-full rounded-lg border border-[#D1D5DB] px-3 py-3 font-mono text-sm"
      />
      <button onClick={guardar} disabled={!direccion || cargando}
        className="mt-3 w-full rounded-lg bg-[#092F7B] text-white font-semibold py-3 disabled:opacity-40">
        {cargando ? 'Guardando…' : etiqueta}
      </button>
    </div>
  );
}
